import { Box, Popover, Spinner } from '@bechtel/gds';
import { EngineeringStatus } from '@bechtel/sr-types';
import { concat, includes, noop, reduce } from 'lodash';
import { FC, PropsWithChildren, lazy, useMemo, useRef, useState } from 'react';
import { FormMode, MultiLingualText, useAppUser, useChangeManagementToolbar } from '~/components';
//@ts-ignore images ignore
import { ReactComponent as ApproveIcon } from '~/images/icons/approve.svg';
//@ts-ignore images ignore
import { ReactComponent as CheckIcon } from '~/images/icons/check.svg';
//@ts-ignore images ignore
import { ReactComponent as IssueIcon } from '~/images/icons/issue.svg';
//@ts-ignore images ignore
import { ReactComponent as PrepareIcon } from '~/images/icons/prepare.svg';
//@ts-ignore images ignore
import { ReactComponent as ReturnByApproverIcon } from '~/images/icons/return-by-approver.svg';
//@ts-ignore images ignore
import { ReactComponent as UnPrepareIcon } from '~/images/icons/un-prepare.svg';
//@ts-ignore images ignore
import { ReactComponent as NoteIcon } from '~/images/icons/note.svg';
//@ts-ignore images ignore
import { ReactComponent as ReturnByCheckerIcon } from '~/images/icons/return-by-checker.svg';
import { ButtonIconStyled, ContainerStyled, DividerStyled } from './styled';
import { ChangeManagementBulkToolBarProps, ChangeManagementToolBarItemProps } from './types';
//@ts-ignore images ignore
import { useChangeManagement } from '~/hooks';
import { AddChangeManagementNoteModal, ViewChangeManagementNoteModal } from './change-management-notes';
import {
  Operation,
  apprEnableValues,
  checkEnableValues,
  commoditiesWithNotes,
  getIssueData,
  getIssueErrors,
  getIssueInput,
  prepareEnableValues,
} from './utils';
const MessageModal = lazy(() => import('~/components/organisms/message-modal/message'));
const BulkMessageModal = lazy(() => import('~/components/organisms/message-modal/bulk-message-modal'));

const ChangeManagementToolBarItem: FC<PropsWithChildren<ChangeManagementToolBarItemProps>> = ({
  onClick,
  operation,
  children,
  disabled = false,
  value,
  status = null,
}) => {
  const ref = useRef(null);
  const [showPopOver, setShowPopOver] = useState<boolean>(false);
  const absValue = value ? Math.abs(+value).toString() : null;
  return (
    <>
      <ButtonIconStyled
        onClick={() => {
          setShowPopOver(false);
          onClick(value, operation);
        }}
        onMouseOver={() => setShowPopOver(true)}
        onMouseLeave={() => setShowPopOver(false)}
        active={absValue !== null && status === absValue}
        disabled={disabled}
      >
        <div ref={ref}>{children}</div>
      </ButtonIconStyled>
      <Popover
        triggerComponentRef={ref}
        showPopper={showPopOver}
        customStyle={{ backgroundColor: 'black', color: 'white', zIndex: 1 }}
        placement="bottom"
      >
        <Box py={{ sm: '300' }} px={{ sm: '400' }} css={{ textAlign: 'center' }}>
          <MultiLingualText size="300" color="white" family="openSansCondensed">
            {operation}
          </MultiLingualText>
        </Box>
      </Popover>
    </>
  );
};

interface ChangeManagementToolBarProps {
  mode?: string | undefined;
}
export const ChangeManagementToolBar: FC<ChangeManagementToolBarProps> = ({ mode = FormMode.UPDATE }) => {
  const { permissions, engineeringStatus, signature, name, id, enabled, onIssue } = useChangeManagementToolbar();
  const { canCheck, canApprove, canPrepare, canIssue } = permissions;
  const { userAccount } = useAppUser();
  const [issueCommodities, { loading, data }] = useChangeManagement(name);
  const [showViewNotes, setShowViewNotes] = useState(false);
  const [addNote, setAddNote] = useState({ show: false, value: EngineeringStatus.UNPREPARED });
  const [operation, setOperation] = useState('');

  const issueHandler = async (engStatus: EngineeringStatus, operation: string) => {
    // For rejection show notes modal for main commodities
    setOperation(operation);
    if (
      (engStatus === EngineeringStatus.RETURNED_BY_CHECKER || engStatus === EngineeringStatus.RETURNED_BY_APPROVER) &&
      includes(commoditiesWithNotes, name)
    ) {
      setAddNote({ show: true, value: engStatus });
      return;
    }
    const issueInput = getIssueInput(name, [id], engStatus);
    await issueCommodities({
      variables: {
        ...issueInput,
      },
      onCompleted: (data) => {
        const issueData = getIssueData(name, data);
        if (onIssue) {
          onIssue(issueData);
        }
      },
    });
  };

  const issueNoteHandler = async (note: string) => {
    setAddNote({ show: false, value: EngineeringStatus.UNPREPARED });
    const issueInput = getIssueInput(name, [id], addNote.value, note);
    await issueCommodities({
      variables: {
        ...issueInput,
      },
    });
  };

  const disableCheck = (fieldStatus) => {
    switch (fieldStatus) {
      case EngineeringStatus.CHECKED:
      case EngineeringStatus.RETURNED_BY_CHECKER:
        return (
          !includes(checkEnableValues, engineeringStatus) ||
          !canCheck ||
          signature?.preparedBy === userAccount?.loginName ||
          mode != FormMode.UPDATE
        );
      case EngineeringStatus.UNDO_CHECKED:
      case EngineeringStatus.UNDO_RETURNED_BY_CHECKER:
        return (
          !includes(checkEnableValues, engineeringStatus) ||
          !canCheck ||
          signature?.checkedBy !== userAccount?.loginName ||
          mode != FormMode.UPDATE
        );
      case EngineeringStatus.APPROVED:
      case EngineeringStatus.RETURNED_BY_APPROVER:
        return userAccount?.tenantConfig?.isChkrApprovalAllowed
          ? !includes(apprEnableValues, engineeringStatus) ||
              !canApprove ||
              signature?.preparedBy === userAccount?.loginName ||
              mode != FormMode.UPDATE
          : !includes(apprEnableValues, engineeringStatus) ||
              !canApprove ||
              signature?.preparedBy === userAccount?.loginName ||
              signature?.checkedBy === userAccount?.loginName ||
              mode != FormMode.UPDATE;
      case EngineeringStatus.UNDO_APPROVED:
      case EngineeringStatus.UNDO_RETURNED_BY_APPROVER:
        return (
          engineeringStatus === EngineeringStatus.RETURNED_BY_CHECKER ||
          !canApprove ||
          signature?.approvedBy !== userAccount?.loginName ||
          mode != FormMode.UPDATE
        );
      default:
        return false;
    }
  };

  const errorKeys = useMemo(() => {
    const issueErrors = getIssueErrors(name, data);
    if (!issueErrors || issueErrors.length === 0) return;
    const result = reduce(issueErrors, (a: any, b) => concat(a, b.messageKeys), []);
    return result;
  }, [data, name]);

  if (!enabled) return;
  return (
    <>
      <ContainerStyled>
        {includes(prepareEnableValues, engineeringStatus) ? (
          <ChangeManagementToolBarItem
            disabled={loading || !canPrepare || mode != FormMode.UPDATE}
            onClick={issueHandler}
            operation={Operation.PRERARE}
            value={EngineeringStatus.PREPARED}
          >
            {loading && operation === Operation.PRERARE ? <Spinner /> : <PrepareIcon />}
          </ChangeManagementToolBarItem>
        ) : (
          <ChangeManagementToolBarItem
            value={EngineeringStatus.UNPREPARED}
            disabled={loading || mode != FormMode.UPDATE}
            onClick={issueHandler}
            operation={Operation.UNPREPARE}
          >
            {loading && operation === Operation.UNPREPARE ? <Spinner /> : <UnPrepareIcon />}
          </ChangeManagementToolBarItem>
        )}
        <DividerStyled />

        <ChangeManagementToolBarItem
          onClick={issueHandler}
          operation={`${
            engineeringStatus === EngineeringStatus.RETURNED_BY_CHECKER
              ? Operation.UNDO_RETURN_BY_CHECKER
              : Operation.RETURN_BY_CHECKER
          }`}
          disabled={
            loading || engineeringStatus === EngineeringStatus.RETURNED_BY_CHECKER
              ? disableCheck(EngineeringStatus.UNDO_RETURNED_BY_CHECKER)
              : disableCheck(EngineeringStatus.RETURNED_BY_CHECKER)
          }
          value={
            engineeringStatus === EngineeringStatus.RETURNED_BY_CHECKER
              ? EngineeringStatus.UNDO_RETURNED_BY_CHECKER
              : EngineeringStatus.RETURNED_BY_CHECKER
          }
          status={engineeringStatus}
        >
          {loading && (operation === Operation.UNDO_RETURN_BY_CHECKER || operation === Operation.RETURN_BY_CHECKER) ? (
            <Spinner />
          ) : (
            <ReturnByCheckerIcon />
          )}
        </ChangeManagementToolBarItem>
        <ChangeManagementToolBarItem
          onClick={issueHandler}
          operation={`${engineeringStatus === EngineeringStatus.CHECKED ? Operation.UNDO_CHECK : Operation.CHECK}`}
          disabled={
            loading || engineeringStatus === EngineeringStatus.CHECKED
              ? disableCheck(EngineeringStatus.UNDO_CHECKED)
              : disableCheck(EngineeringStatus.CHECKED)
          }
          value={
            engineeringStatus === EngineeringStatus.CHECKED ? EngineeringStatus.UNDO_CHECKED : EngineeringStatus.CHECKED
          }
          status={engineeringStatus}
        >
          {loading && (operation === Operation.UNDO_CHECK || operation === Operation.CHECK) ? (
            <Spinner />
          ) : (
            <CheckIcon />
          )}
        </ChangeManagementToolBarItem>
        <DividerStyled />

        <ChangeManagementToolBarItem
          onClick={issueHandler}
          operation={`${
            engineeringStatus === EngineeringStatus.RETURNED_BY_APPROVER
              ? Operation.UNDO_RETURN_BY_APPROVER
              : Operation.RETURN_BY_APPROVER
          }`}
          disabled={
            loading || engineeringStatus === EngineeringStatus.RETURNED_BY_APPROVER
              ? disableCheck(EngineeringStatus.UNDO_RETURNED_BY_APPROVER)
              : disableCheck(EngineeringStatus.RETURNED_BY_APPROVER)
          }
          value={
            engineeringStatus === EngineeringStatus.RETURNED_BY_APPROVER
              ? EngineeringStatus.UNDO_RETURNED_BY_APPROVER
              : EngineeringStatus.RETURNED_BY_APPROVER
          }
          status={engineeringStatus}
        >
          {loading &&
          (operation === Operation.UNDO_RETURN_BY_APPROVER || operation === Operation.RETURN_BY_APPROVER) ? (
            <Spinner />
          ) : (
            <ReturnByApproverIcon />
          )}
        </ChangeManagementToolBarItem>
        <ChangeManagementToolBarItem
          onClick={issueHandler}
          operation={`${engineeringStatus === EngineeringStatus.APPROVED ? Operation.UNDO_APPROVE : Operation.APPROVE}`}
          disabled={
            loading || engineeringStatus === EngineeringStatus.APPROVED
              ? disableCheck(EngineeringStatus.UNDO_APPROVED)
              : disableCheck(EngineeringStatus.APPROVED)
          }
          value={
            engineeringStatus === EngineeringStatus.APPROVED
              ? EngineeringStatus.UNDO_APPROVED
              : EngineeringStatus.APPROVED
          }
          status={engineeringStatus}
        >
          {loading && (operation === Operation.APPROVE || operation === Operation.UNDO_APPROVE) ? (
            <Spinner />
          ) : (
            <ApproveIcon />
          )}
        </ChangeManagementToolBarItem>
        <DividerStyled />
        <ChangeManagementToolBarItem
          onClick={issueHandler}
          operation={Operation.ISSUE}
          disabled={engineeringStatus !== EngineeringStatus.APPROVED || !canIssue || loading || mode != FormMode.UPDATE}
          value={EngineeringStatus.ISSUED}
        >
          {loading && operation === Operation.ISSUE ? <Spinner /> : <IssueIcon />}
        </ChangeManagementToolBarItem>
        <DividerStyled />
        <ChangeManagementToolBarItem
          onClick={() => setShowViewNotes(true)}
          operation="Change Management Notes"
          disabled={mode != FormMode.UPDATE || false}
        >
          <NoteIcon />
        </ChangeManagementToolBarItem>
      </ContainerStyled>
      {showViewNotes && <ViewChangeManagementNoteModal id={id} name={name} onClose={() => setShowViewNotes(false)} />}
      {addNote.show && <AddChangeManagementNoteModal onSubmit={issueNoteHandler} />}
      {errorKeys && errorKeys.length > 0 && <MessageModal messageKeys={errorKeys}></MessageModal>}
    </>
  );
};

export const ChangeManagementBulkToolBar: FC<ChangeManagementBulkToolBarProps> = ({
  ids,
  name,
  permissions,
  onIssue,
}) => {
  const { canCheck, canApprove, canPrepare, canIssue } = permissions;
  const [issueCommodities, { loading, data }] = useChangeManagement(name);
  const [addNote, setAddNote] = useState({ show: false, value: EngineeringStatus.UNPREPARED });
  const [operation, setOperation] = useState('');

  const issueHandler = async (engStatus: EngineeringStatus) => {
    setOperation(operation);
    // For rejection show notes modal for main commodities
    if (
      (engStatus === EngineeringStatus.RETURNED_BY_CHECKER || engStatus === EngineeringStatus.RETURNED_BY_APPROVER) &&
      includes(commoditiesWithNotes, name)
    ) {
      setAddNote({ show: true, value: engStatus });
      return;
    }
    const issueInput = getIssueInput(name, ids, engStatus);
    await issueCommodities({
      variables: {
        ...issueInput,
      },
      onCompleted: (data) => {
        const issueData = getIssueData(name, data);
        onIssue(issueData);
      },
    });
  };

  const issueNoteHandler = async (note: string) => {
    setAddNote({ show: false, value: EngineeringStatus.UNPREPARED });
    const issueInput = getIssueInput(name, ids, addNote.value, note);
    await issueCommodities({
      variables: {
        ...issueInput,
      },
      onCompleted: (data) => {
        const issueData = getIssueData(name, data);
        onIssue(issueData);
      },
    });
  };

  const errorKeys = useMemo(() => {
    const issueErrors = getIssueErrors(name, data);
    if (!issueErrors || issueErrors.length === 0) return;
    return issueErrors;
  }, [data, name]);

  return (
    <>
      <ContainerStyled>
        <ChangeManagementToolBarItem
          disabled={loading || !canPrepare}
          onClick={issueHandler}
          operation={Operation.PRERARE}
          value={EngineeringStatus.PREPARED}
        >
          {loading && operation === Operation.PRERARE ? <Spinner /> : <PrepareIcon />}
        </ChangeManagementToolBarItem>
        <DividerStyled />
        <ChangeManagementToolBarItem
          onClick={issueHandler}
          operation={Operation.RETURN_BY_CHECKER}
          disabled={loading || !canCheck}
          value={EngineeringStatus.RETURNED_BY_CHECKER}
        >
          {loading && operation === Operation.RETURN_BY_CHECKER ? <Spinner /> : <ReturnByCheckerIcon />}
        </ChangeManagementToolBarItem>
        <ChangeManagementToolBarItem
          onClick={issueHandler}
          operation={Operation.CHECK}
          disabled={loading || !canCheck}
          value={EngineeringStatus.CHECKED}
        >
          {loading && operation === Operation.CHECK ? <Spinner /> : <CheckIcon />}
        </ChangeManagementToolBarItem>
        <DividerStyled />

        <ChangeManagementToolBarItem
          onClick={issueHandler}
          operation={Operation.RETURN_BY_APPROVER}
          disabled={loading || !canApprove}
          value={EngineeringStatus.RETURNED_BY_APPROVER}
        >
          {loading && operation === Operation.RETURN_BY_APPROVER ? <Spinner /> : <ReturnByApproverIcon />}
        </ChangeManagementToolBarItem>
        <ChangeManagementToolBarItem
          onClick={issueHandler}
          operation={Operation.APPROVE}
          disabled={!canApprove}
          value={EngineeringStatus.APPROVED}
        >
          {loading && operation === Operation.APPROVE ? <Spinner /> : <ApproveIcon />}
        </ChangeManagementToolBarItem>
        <DividerStyled />
        <ChangeManagementToolBarItem
          onClick={issueHandler}
          operation={Operation.ISSUE}
          disabled={!canIssue}
          value={EngineeringStatus.ISSUED}
        >
          {loading && operation === Operation.ISSUE ? <Spinner /> : <IssueIcon />}
        </ChangeManagementToolBarItem>
        <DividerStyled />
        <ChangeManagementToolBarItem onClick={noop} operation="Note" disabled={true}>
          <NoteIcon />
        </ChangeManagementToolBarItem>
      </ContainerStyled>
      {addNote.show && <AddChangeManagementNoteModal onSubmit={issueNoteHandler} />}
      {errorKeys && errorKeys.length > 0 && <BulkMessageModal messages={errorKeys}></BulkMessageModal>}
    </>
  );
};
